import { FileText, UserPlus } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { CLAIMABLE_COMPLETION_PCT } from "../../../handlers/rewards";

interface EarnCardsProps {
	resumePct: number;
	onBuildResume: () => void;
	onRefer: () => void;
}

const REWARD_VALUE = 150;

interface EarnRowProps {
	icon: React.ReactNode;
	title: string;
	points: number;
	description: string;
	action: string;
	onAction: () => void;
	/** Exactly one row is primary — the step this account should take next. */
	primary: boolean;
}

function EarnRow({ icon, title, points, description, action, onAction, primary }: EarnRowProps) {
	return (
		<div className="border-b border-gray-100 py-6 first:pt-0 last:border-b-0 last:pb-0">
			<div className="flex items-center gap-2">
				<div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
					{icon}
				</div>
				<h3 className="text-sm font-bold text-gray-900 font-plus-jakarta">{title}</h3>
				<span className="ml-auto rounded-full bg-primary-50 px-2 py-0.5 text-xs font-bold text-primary-700 tabular-nums">
					+{points}
				</span>
			</div>
			<p className="mt-2 text-sm text-gray-600 font-inter leading-relaxed">{description}</p>
			<Button variant={primary ? "default" : "outlineSecondary"} className="mt-3 w-full" onClick={onAction}>
				{action}
			</Button>
		</div>
	);
}

/**
 * The ways a reader can still move the balance, led by whichever is worth the most attention.
 *
 * The resume leads while it is unfinished: it is the same task the profile page already asks for, and
 * it releases points the account is already holding. Past the threshold the points are earned and
 * referring takes both the first slot and the emphasis, though the resume row stays while there is
 * still something to write. At 100% it goes entirely — a card headed "Ways to earn" should not hold a
 * row that cannot earn anything, and there is nothing left to review it for.
 */
function EarnCards({ resumePct, onBuildResume, onRefer }: EarnCardsProps) {
	const resumeDone = resumePct >= CLAIMABLE_COMPLETION_PCT;
	const resumeComplete = resumePct >= 100;

	const resume = (
		<EarnRow
			key="resume"
			icon={<FileText className="size-3.5" />}
			title="Finish your resume"
			points={REWARD_VALUE}
			description={
				resumeDone
					? `Your resume is past ${CLAIMABLE_COMPLETION_PCT}%, so these points are yours to collect.`
					: `Take it to ${CLAIMABLE_COMPLETION_PCT}% and collect ${REWARD_VALUE} points. You are at ${resumePct}%.`
			}
			action={resumeDone ? "Review your resume" : "Continue"}
			onAction={onBuildResume}
			primary={!resumeDone}
		/>
	);

	const refer = (
		<EarnRow
			key="refer"
			icon={<UserPlus className="size-3.5" />}
			title="Refer a friend"
			points={REWARD_VALUE}
			description={`We email them an invitation. You collect ${REWARD_VALUE} points once their resume reaches ${CLAIMABLE_COMPLETION_PCT}%.`}
			action="Refer a friend"
			onAction={onRefer}
			primary={resumeDone}
		/>
	);

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-6">
			<h2 className="text-sm font-medium text-gray-600 font-inter">Ways to earn</h2>
			<div className="mt-3">{resumeComplete ? [refer] : resumeDone ? [refer, resume] : [resume, refer]}</div>
		</div>
	);
}

export default EarnCards;
